import type { ReactNode } from 'react';
import { Badge, Skeleton, TBody, TD, TH, THead, TR, Table, TableWrap } from '@/components/ui';
import { EmailLink, TextLink } from '@/components/ui/TextLink';
import { formatDateTime } from '@/lib/format';
import type { NotificationLogRow } from '@/lib/types';
import { CHANNEL_LABELS, OUTCOMES, recordPath } from './log-labels';

const TABLE_STYLE = { minWidth: '960px' };
const SKELETON_ROWS = 8;

export interface LogUser {
  id: number;
  name: string;
  email: string;
}

interface Props {
  rows: readonly NotificationLogRow[];
  loading: boolean;
  /** Event key → catalogue label. */
  eventLabels: ReadonlyMap<string, string>;
  /** User id → user, for rows the server sent without `user`. */
  users: ReadonlyMap<number, LogUser>;
}

function Recipient({ row, users }: { row: NotificationLogRow; users: ReadonlyMap<number, LogUser> }) {
  const user = row.user ?? (row.userId !== null ? users.get(row.userId) : undefined) ?? null;
  if (user) {
    return (
      <div className="min-w-0">
        <p className="m-0 text-sm text-fg [overflow-wrap:anywhere]">{user.name.trim() || user.email}</p>
        <EmailLink address={user.email} tone="muted" className="text-xs" />
      </div>
    );
  }
  if (row.address) return <EmailLink address={row.address} />;
  if (row.userId !== null) return <span className="text-fg-muted">User #{row.userId}</span>;
  return <span className="text-fg-subtle">—</span>;
}

function RecordCell({ row }: { row: NotificationLogRow }) {
  const path = recordPath(row.entityType, row.entityId);
  if (!row.entityType || row.entityId === null) return <span className="text-fg-subtle">—</span>;
  const label = `${row.entityType.replace(/[_-]/g, ' ')} #${row.entityId}`;
  return path ? (
    <TextLink to={path} className="capitalize">
      {label}
    </TextLink>
  ) : (
    <span className="capitalize text-fg-secondary">{label}</span>
  );
}

function Outcome({ row }: { row: NotificationLogRow }) {
  const outcome = OUTCOMES[row.decision] ?? { label: row.decision, tone: 'neutral' as const };
  return (
    <div className="grid min-w-0 justify-items-start gap-0.5">
      <Badge tone={outcome.tone} dot>
        {outcome.label}
      </Badge>
      {row.reason && (
        <span className="text-xs text-fg-muted [overflow-wrap:anywhere]">{row.reason}</span>
      )}
    </div>
  );
}

function eventLabel(row: NotificationLogRow, labels: ReadonlyMap<string, string>): ReactNode {
  return labels.get(row.eventKey) ?? <code className="text-xs">{row.eventKey}</code>;
}

export function NotificationLogList({ rows, loading, eventLabels, users }: Props) {
  if (loading && rows.length === 0) {
    return (
      <>
        <div className="hidden md:block">
          <TableWrap frozenHeader>
            <Table style={TABLE_STYLE}>
              <LogTableHead />
              <TBody>
                {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                  <TR key={i}>
                    {Array.from({ length: 6 }, (_, j) => (
                      <TD key={j}>
                        <Skeleton className="h-3 w-24" />
                      </TD>
                    ))}
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableWrap>
        </div>
        <div className="grid gap-2 p-4 md:hidden">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="hidden md:block">
        <TableWrap frozenHeader>
          <Table style={TABLE_STYLE}>
            <LogTableHead />
            <TBody>
              {rows.map((row) => (
                <TR key={row.id}>
                  <TD className="whitespace-nowrap tabular text-fg-secondary">
                    {formatDateTime(row.createdAt)}
                  </TD>
                  <TD>
                    <span className="[overflow-wrap:anywhere]">{eventLabel(row, eventLabels)}</span>
                  </TD>
                  <TD>
                    <Recipient row={row} users={users} />
                  </TD>
                  <TD>{CHANNEL_LABELS[row.channel] ?? row.channel}</TD>
                  <TD>
                    <Outcome row={row} />
                  </TD>
                  <TD>
                    <RecordCell row={row} />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      </div>

      <ul className="m-0 grid list-none gap-2 p-4 md:hidden">
        {rows.map((row) => (
          <li key={row.id} className="grid gap-2 rounded-lg border border-border bg-surface p-3">
            <div className="flex items-start justify-between gap-2">
              <p className="m-0 min-w-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
                {eventLabel(row, eventLabels)}
              </p>
              <Outcome row={row} />
            </div>
            <dl className="m-0 grid grid-cols-[6rem_minmax(0,1fr)] gap-x-2 gap-y-1 text-xs">
              <dt className="text-fg-muted">When</dt>
              <dd className="m-0 tabular">{formatDateTime(row.createdAt)}</dd>
              <dt className="text-fg-muted">Recipient</dt>
              <dd className="m-0 min-w-0">
                <Recipient row={row} users={users} />
              </dd>
              <dt className="text-fg-muted">Channel</dt>
              <dd className="m-0">{CHANNEL_LABELS[row.channel] ?? row.channel}</dd>
              <dt className="text-fg-muted">Record</dt>
              <dd className="m-0">
                <RecordCell row={row} />
              </dd>
            </dl>
          </li>
        ))}
      </ul>
    </>
  );
}

function LogTableHead() {
  return (
    <THead>
      <TR>
        <TH className="w-44">When</TH>
        <TH>Event</TH>
        <TH className="w-56">Recipient</TH>
        <TH className="w-24">Channel</TH>
        <TH className="w-44">Outcome</TH>
        <TH className="w-40">Record</TH>
      </TR>
    </THead>
  );
}
